import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants } from './ptlSetupApi'
import { ROUTES, fmtDate } from './constants'
import { Banner, Breadcrumb } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import TatStep, { tatKpis, useTat } from './steps/TatStep'
import TatMatrix from './tat/TatMatrix'

/**
 * PTL FREIGHT → PTL Merchant Setup → Custom TAT → one merchant (plan screen 10, route /tat/custom/:merchantId). The same
 * body as onboarding Step 2 (Standard / Courier / Custom TAT, with the one in use selected, custom lanes, upload,
 * history, remove), plus the effective TAT of every lane as the matrix.
 */
export default function PtlTatCustomMerchant() {
  const { merchantId } = useParams()
  const navigate = useNavigate()
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const tat = useTat(merchantId)

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then(setMerchants).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { load() }, [load])

  const merchant = useMemo(() => merchants?.find((m) => m.id === merchantId), [merchants, merchantId])
  const crumb = <Breadcrumb parent='Custom TAT' onParent={() => navigate(ROUTES.tatCustom)} current={merchant?.name || 'Merchant'} />
  const grid = tat.grid
  const latest = grid?.latest

  if (merchants && !merchant) {
    return (
      <PtlPage title='Merchant TAT' breadcrumb={crumb}>
        <EmptyState title='Not a live PTL merchant'
          body='This merchant is not in the PTL merchant list. Turn on PTL for it from Onboarding → Onboard a merchant first, or pick another merchant.'
          action={<Button variant='contained' onClick={() => navigate(ROUTES.tatCustom)} sx={SX.cta}>Back to custom TAT</Button>} />
      </PtlPage>
    )
  }

  const kpis = [
    ...tatKpis(grid),
    { key: 'last', label: 'Last change', value: latest ? fmtDate(latest.createdAt).split(',')[0] : 'None',
      sub: latest ? latest.createdBy || '' : 'No custom TAT uploaded yet' },
  ]

  return (
    <PtlPage title={merchant ? `TAT · ${merchant.name}` : 'Merchant TAT'} breadcrumb={crumb}
      description='Each lane gets one TAT: the custom TAT (C) where the merchant has one, else the courier TAT (P) it is on, else the Prozo standard (S).'
      kpis={kpis} kpisLoading={!grid && !tat.error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}

      {merchant && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Card elevation={0} sx={{ ...SX.card, p: 2 }}>
            <TatStep merchant={merchant} {...tat} onChanged={load} onUploadView={setUploading} />
          </Card>
          {grid && !uploading && grid.cells.length > 0 && (
            <Card elevation={0} sx={{ ...SX.card, p: 2 }}>
              <Typography component='h2' sx={{ ...TYPE.sectionTitle, mb: 1.25 }}>Effective TAT by lane</Typography>
              <TatMatrix grid={grid} showSource />
            </Card>
          )}
        </Box>
      )}
    </PtlPage>
  )
}
